import { useEffect, type ReactNode } from "react";

export interface ModalProps {
  /** Whether the modal is shown. */
  open: boolean;
  /** Dialog heading. */
  title: string;
  /** Close request (overlay click, Esc, or the × button). */
  onClose: () => void;
  /** Body content. */
  children: ReactNode;
  /** Optional action row pinned to the bottom of the dialog. */
  footer?: ReactNode;
}

/**
 * jve-6-5: a centred modal dialog — dimmed overlay + a surface card with a titled header (× close)
 * and an optional footer action row. Closes on overlay click and Escape. Presentational +
 * extraction-ready; mirrors the Jmix dialog vocabulary (add/edit, delete-with-usage).
 */
export function Modal({ open, title, onClose, children, footer }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fui-modal-overlay" onClick={onClose}>
      <div
        className="fui-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fui-modal__header">
          <h2 className="fui-modal__title">{title}</h2>
          <button type="button" className="fui-modal__close" aria-label="Sluiten" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="fui-modal__body">{children}</div>
        {footer && <div className="fui-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}
